import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { db } from '@/lib/firebase';
import {
  collection, query, where, getDocs, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp,
} from 'firebase/firestore';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Calendar, MapPin, Clock, Plus, Edit2, Trash2, Info } from 'lucide-react';
import { isGigExpired, normalizeTime24, formatGigTime12h } from '@/lib/gigUtils';
import { ysr2Mobile } from '@/features/shared/styles/mobileDesignSystem';

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const [y, m, d] = String(dateString).split('-').map(Number);
    if (!y || !m || !d) return dateString;
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateString;
  }
};

const EMPTY_GIG = { venue_name: '', date: '', time: '', address: '', postcode: '', city: '' };

/**
 * Upcoming gigs editor for /admin/profile (and legacy artist profile route if used).
 * Same data as Calendar — powers /find-performer.
 */
const ProfileGigsSection = ({ variant = 'default' }) => {
  const isMobile = variant === 'mobile';
  const { user } = useAuth();
  const { toast } = useToast();
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_GIG);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (!user?.uid) return;
    loadGigs();
  }, [user?.uid]);

  const loadGigs = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'gigs'), where('user_id', '==', user.uid));
      const snap = await getDocs(q);
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const upcoming = all.filter(g => !isGigExpired(g.date));
      upcoming.sort((a, b) => String(a.date).localeCompare(String(b.date)));
      setGigs(upcoming);
    } catch (err) {
      console.error('Error loading gigs:', err);
      toast({ title: 'Could not load gigs', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.venue_name || !form.date) {
      toast({ title: 'Required fields missing', description: 'Venue name and date are required.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        time: normalizeTime24(form.time) || '',
        venue: form.venue_name,
        location: [form.address, form.postcode, form.city].filter(Boolean).join(', '),
        user_id: user.uid,
        updated_at: serverTimestamp(),
      };

      if (editingId) {
        await updateDoc(doc(db, 'gigs', editingId), payload);
        toast({ title: 'Gig updated' });
      } else {
        await addDoc(collection(db, 'gigs'), {
          ...payload,
          created_at: serverTimestamp(),
        });
        toast({ title: 'Gig added', description: 'Your gig will appear on /find-performer and in Calendar.' });
      }
      setForm(EMPTY_GIG);
      setShowForm(false);
      setEditingId(null);
      loadGigs();
    } catch (err) {
      console.error('Error saving gig:', err);
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (gig) => {
    setForm({
      venue_name: gig.venue_name || gig.venue || '',
      date: gig.date || '',
      time: normalizeTime24(gig.time || '') || '',
      address: gig.address || '',
      postcode: gig.postcode || '',
      city: gig.city || '',
    });
    setEditingId(gig.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this gig from /find-performer?')) return;
    setDeletingId(id);
    try {
      await deleteDoc(doc(db, 'gigs', id));
      toast({ title: 'Gig removed' });
      setGigs(prev => prev.filter(g => g.id !== id));
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };

  const handleCancel = () => {
    setForm(EMPTY_GIG);
    setEditingId(null);
    setShowForm(false);
  };

  const formFields = (
    <>
      <div>
        <Label className={isMobile ? 'text-blue-100/65 text-xs' : 'text-gray-400 text-xs'}>
          Venue Name <span className="text-red-400">*</span>
        </Label>
        {isMobile ? (
          <input
            value={form.venue_name}
            onChange={e => setForm(p => ({ ...p, venue_name: e.target.value }))}
            placeholder="e.g. The Red Lion"
            className={`w-full mt-1 ${ysr2Mobile.input} text-base`}
          />
        ) : (
          <Input
            value={form.venue_name}
            onChange={e => setForm(p => ({ ...p, venue_name: e.target.value }))}
            placeholder="e.g. The Red Lion"
            className="bg-gray-800 border-gray-600 text-white mt-1"
          />
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className={isMobile ? 'text-blue-100/65 text-xs' : 'text-gray-400 text-xs'}>
            Date <span className="text-red-400">*</span>
          </Label>
          {isMobile ? (
            <input
              type="date"
              value={form.date}
              onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
              className={`w-full mt-1 ${ysr2Mobile.input} text-base`}
            />
          ) : (
            <Input
              type="date"
              value={form.date}
              onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
              className="bg-gray-800 border-gray-600 text-white mt-1"
            />
          )}
        </div>
        <div>
          <Label className={isMobile ? 'text-blue-100/65 text-xs' : 'text-gray-400 text-xs'}>Start Time</Label>
          {isMobile ? (
            <input
              type="time"
              value={form.time}
              onChange={e => setForm(p => ({ ...p, time: e.target.value }))}
              className={`w-full mt-1 ${ysr2Mobile.input} text-base`}
            />
          ) : (
            <Input
              type="time"
              value={form.time}
              onChange={e => setForm(p => ({ ...p, time: e.target.value }))}
              className="bg-gray-800 border-gray-600 text-white mt-1"
            />
          )}
        </div>
      </div>
      <div>
        <Label className={isMobile ? 'text-blue-100/65 text-xs' : 'text-gray-400 text-xs'}>Street Address</Label>
        {isMobile ? (
          <input
            value={form.address}
            onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
            placeholder="e.g. 123 High Street"
            className={`w-full mt-1 ${ysr2Mobile.input} text-base`}
          />
        ) : (
          <Input
            value={form.address}
            onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
            placeholder="e.g. 123 High Street"
            className="bg-gray-800 border-gray-600 text-white mt-1"
          />
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className={isMobile ? 'text-blue-100/65 text-xs' : 'text-gray-400 text-xs'}>Postcode</Label>
          {isMobile ? (
            <input
              value={form.postcode}
              onChange={e => setForm(p => ({ ...p, postcode: e.target.value }))}
              placeholder="SW1A 1AA"
              className={`w-full mt-1 ${ysr2Mobile.input} text-base`}
            />
          ) : (
            <Input
              value={form.postcode}
              onChange={e => setForm(p => ({ ...p, postcode: e.target.value }))}
              placeholder="SW1A 1AA"
              className="bg-gray-800 border-gray-600 text-white mt-1"
            />
          )}
        </div>
        <div>
          <Label className={isMobile ? 'text-blue-100/65 text-xs' : 'text-gray-400 text-xs'}>City</Label>
          {isMobile ? (
            <input
              value={form.city}
              onChange={e => setForm(p => ({ ...p, city: e.target.value }))}
              placeholder="London"
              className={`w-full mt-1 ${ysr2Mobile.input} text-base`}
            />
          ) : (
            <Input
              value={form.city}
              onChange={e => setForm(p => ({ ...p, city: e.target.value }))}
              placeholder="London"
              className="bg-gray-800 border-gray-600 text-white mt-1"
            />
          )}
        </div>
      </div>
      <p className={isMobile ? 'text-xs text-blue-100/50' : 'text-xs text-gray-500'}>
        Address fields improve directions on /find-performer (map and links work with address or coordinates).
      </p>
      <div className="flex gap-2 pt-1">
        {isMobile ? (
          <>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !form.venue_name || !form.date}
              className={`flex-1 ${ysr2Mobile.primaryButton} px-4 py-2.5 text-sm font-semibold disabled:opacity-45 touch-manipulation`}
            >
              {saving ? 'Saving…' : editingId ? 'Update Gig' : 'Add Gig'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className={`flex-1 ${ysr2Mobile.secondaryButton} px-4 py-2.5 text-sm font-semibold touch-manipulation`}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <Button
              type="button"
              onClick={handleSave}
              disabled={saving || !form.venue_name || !form.date}
              className="bg-green-600 hover:bg-green-700"
              size="sm"
            >
              {saving ? 'Saving…' : editingId ? 'Update Gig' : 'Add Gig'}
            </Button>
            <Button type="button" onClick={handleCancel} variant="outline" size="sm" className="border-gray-600 text-gray-300">
              Cancel
            </Button>
          </>
        )}
      </div>
    </>
  );

  if (isMobile) {
    return (
      <div className={`${ysr2Mobile.surface} relative z-10 overflow-hidden`}>
        <div className="p-4 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-300 shrink-0" />
                Upcoming Gigs
              </h2>
            </div>
            {!showForm && (
              <button
                type="button"
                onClick={() => setShowForm(true)}
                className={`${ysr2Mobile.primaryButton} inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold shrink-0 touch-manipulation`}
              >
                <Plus className="w-4 h-4" />
                Add Gig
              </button>
            )}
          </div>

          <div className={`${ysr2Mobile.amberCard} p-3 flex items-start gap-3`}>
            <Info className="w-4 h-4 text-amber-200 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-50/90 space-y-2">
              <p>
                <strong className="text-white">Adding Gigs is optional.</strong>
              </p>
              <p className="text-blue-100/85 leading-relaxed">
                Tap <strong className="text-white">Add Gig</strong> to list your next show
                <br />
                on our{' '}
                <a
                  href="/find-performer"
                  className="font-semibold text-blue-300 underline decoration-blue-300/50 hover:text-blue-200"
                >
                  Home Page
                </a>
                .
              </p>
            </div>
          </div>

          {showForm && (
            <div className={`${ysr2Mobile.glowCard} p-4 space-y-3 border border-purple-400/20`}>
              <h4 className="text-white font-medium text-sm">{editingId ? 'Edit Gig' : 'Add a Gig'}</h4>
              {formFields}
            </div>
          )}

          {loading ? (
            <div className="text-center py-8 text-blue-100/60 text-sm">Loading gigs…</div>
          ) : gigs.length === 0 ? (
            <div className={`${ysr2Mobile.surfaceStrong} py-10 px-4 text-center border border-white/10`}>
              <Calendar className="w-10 h-10 mx-auto mb-2 text-blue-200/40" />
              <p className="text-sm text-blue-100/75">No upcoming gigs yet.</p>
              <p className="text-xs text-blue-100/55 mt-1">
                Tap <strong className="text-white">Add Gig</strong> to list your next show on our{' '}
                <a href="/find-performer" className="text-blue-300 underline font-medium hover:text-blue-200">
                  Home Page
                </a>
                .
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {gigs.map(gig => (
                <div
                  key={gig.id}
                  className={`${ysr2Mobile.surfaceStrong} flex items-start justify-between gap-3 p-4 border border-blue-300/15`}
                >
                  <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-white truncate">{gig.venue_name || gig.venue}</span>
                      {gig.city && (
                        <span className="text-[10px] uppercase tracking-wide font-semibold text-blue-100/70 border border-blue-300/25 rounded-full px-2 py-0.5">
                          {gig.city}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-blue-100/70 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(gig.date)}
                      </span>
                      {gig.time && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {formatGigTime12h(gig.time)}
                        </span>
                      )}
                    </div>
                    {(gig.address || gig.postcode) && (
                      <div className="flex items-center gap-1 text-xs text-blue-100/50">
                        <MapPin className="w-3.5 h-3.5" />
                        {[gig.address, gig.postcode].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleEdit(gig)}
                      className={`${ysr2Mobile.secondaryButton} h-10 w-10 p-0 inline-flex items-center justify-center touch-manipulation`}
                      aria-label="Edit gig"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(gig.id)}
                      disabled={deletingId === gig.id}
                      className="h-10 w-10 rounded-2xl border border-red-400/30 bg-red-500/10 text-red-200 hover:bg-red-500/20 inline-flex items-center justify-center disabled:opacity-50 touch-manipulation"
                      aria-label="Remove gig"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <Card className="glass-effect border-gray-800">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-white">
              <Calendar className="w-5 h-5 text-blue-400" />
              Upcoming Gigs
            </CardTitle>
          </div>
          {!showForm && (
            <Button
              type="button"
              size="sm"
              onClick={() => setShowForm(true)}
              className="bg-blue-600 hover:bg-blue-700 shrink-0"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Gig
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex items-start gap-3 bg-blue-900/20 border border-blue-800/40 rounded-lg p-3">
          <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-200 space-y-2">
            <p>
              <strong className="text-white">Adding Gigs is optional.</strong>
            </p>
            <p className="text-blue-100/90">
              Tap <strong className="text-white">Add Gig</strong> to list your next show
              <br />
              on our{' '}
              <a href="/find-performer" className="font-semibold text-blue-300 underline hover:text-blue-200">
                Home Page
              </a>
              .
            </p>
          </div>
        </div>

        {showForm && (
          <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4 space-y-3">
            <h4 className="text-white font-medium text-sm">{editingId ? 'Edit Gig' : 'Add a Gig'}</h4>
            {formFields}
          </div>
        )}

        {loading ? (
          <div className="text-center py-6 text-gray-400">Loading gigs…</div>
        ) : gigs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Calendar className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p>No upcoming gigs. Add one above or use Calendar.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {gigs.map(gig => (
              <div
                key={gig.id}
                className="flex items-start justify-between gap-3 bg-gray-900/50 border border-gray-700 rounded-lg p-3"
              >
                <div className="space-y-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white truncate">{gig.venue_name || gig.venue}</span>
                    {gig.city && (
                      <Badge variant="outline" className="border-gray-600 text-gray-400 text-xs">
                        {gig.city}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-400 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(gig.date)}
                    </span>
                    {gig.time && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatGigTime12h(gig.time)}
                      </span>
                    )}
                  </div>
                  {(gig.address || gig.postcode) && (
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <MapPin className="w-3 h-3" />
                      {[gig.address, gig.postcode].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEdit(gig)}
                    className="text-gray-400 hover:text-white h-8 w-8 p-0"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(gig.id)}
                    disabled={deletingId === gig.id}
                    className="text-gray-400 hover:text-red-400 h-8 w-8 p-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProfileGigsSection;
